import type { Address, Hex } from "viem";
import { generatePrivateKey, privateKeyToAccount, type PrivateKeyAccount } from "viem/accounts";

/** The browser keeps only the wallet key, the session credentials, the language and the theme (D50). */
const KEY = "woodpecker-wallet-key";
const SESSIONS = "woodpecker-sessions";

export interface BrowserWallet {
  key: Hex;
  account: PrivateKeyAccount;
  address: Address;
}

export function isPrivateKey(v: string): v is Hex {
  return /^0x[0-9a-fA-F]{64}$/.test(v.trim());
}

export function loadWallet(): BrowserWallet | null {
  try {
    const key = localStorage.getItem(KEY);
    if (!key || !isPrivateKey(key)) return null;
    return walletOf(key);
  } catch {
    return null;
  }
}

export function walletOf(key: Hex): BrowserWallet {
  const account = privateKeyToAccount(key);
  return { key, account, address: account.address };
}

export function saveWallet(key: Hex): BrowserWallet {
  localStorage.setItem(KEY, key);
  return walletOf(key);
}

export function createWallet(): BrowserWallet {
  return saveWallet(generatePrivateKey());
}

export function forgetWallet(): void {
  localStorage.removeItem(KEY);
  localStorage.removeItem(SESSIONS);
}

export interface Session {
  credential: string;
  expiry: string; // ISO 8601
  /** The identity that signed in, when it was not the wallet itself (D47a, D73). */
  identity?: Address;
}

type SessionMap = Record<string, Session>; // `${origin}|${wallet}` → session

function readSessions(): SessionMap {
  try {
    return JSON.parse(localStorage.getItem(SESSIONS) ?? "{}") as SessionMap;
  } catch {
    return {};
  }
}

export function sessionOf(origin: string, wallet: Address): Session | null {
  const s = readSessions()[`${origin}|${wallet.toLowerCase()}`];
  if (!s) return null;
  if (Date.parse(s.expiry) < Date.now() + 60_000) return null;
  return s;
}

export function saveSession(origin: string, wallet: Address, session: Session): void {
  const all = readSessions();
  all[`${origin}|${wallet.toLowerCase()}`] = session;
  localStorage.setItem(SESSIONS, JSON.stringify(all));
}

export function clearSessions(): void {
  localStorage.removeItem(SESSIONS);
}
